// One logo row — the org logo or a brand logo — shared by the org-detail
// panel and the bulk upload (ADR-0164). Presentation only: the parent
// owns the upload (features/admin/logoUpload.ts) and passes the status.
//
// Accessibility:
//   - the row is a labelled group, named by the title;
//   - every button is named "<its label> <title>" (aria-labelledby with
//     two ids), so a list of rows never yields three identical
//     «Subir logo» buttons;
//   - every button is described by the row's status text;
//   - while uploading the primary button is aria-disabled, not disabled,
//     so it keeps focus and the screen reader hears the new status;
//   - no live region inside the row — the parent announces outcomes once.

import { useRef, type ReactNode } from "react";
import { LOGO_ACCEPT_ATTR } from "../features/admin/logoUpload";

export type LogoUploadStatusKind = "idle" | "pending" | "uploading" | "done" | "error" | "skipped";

export type LogoUploadStatus = { kind: LogoUploadStatusKind; message?: string | undefined };

export type LogoUploadRowProps = {
  itemKey: string;
  title: string;
  pill?: ReactNode;
  previewUrl: string | null;
  status?: LogoUploadStatus | undefined;
  primaryLabel: string;
  onPick: (file: File) => void;
  onRetry?: (() => void) | undefined;
  onSkip?: (() => void) | undefined;
  extraActions?: ReactNode;
  disabled?: boolean | undefined;
};

const STATUS_PILL: Record<LogoUploadStatusKind, { className: string; label: string }> = {
  idle: { className: "st-pill st-pill--idle", label: "Sin logo" },
  pending: { className: "st-pill st-pill--info", label: "En cola" },
  uploading: { className: "st-pill st-pill--info", label: "Subiendo…" },
  done: { className: "st-pill st-pill--ok", label: "Subido" },
  error: { className: "st-pill st-pill--warn", label: "No se pudo subir" },
  skipped: { className: "st-pill st-pill--idle", label: "Omitido" },
};

export function LogoUploadRow({
  itemKey,
  title,
  pill,
  previewUrl,
  status,
  primaryLabel,
  onPick,
  onRetry,
  onSkip,
  extraActions,
  disabled = false,
}: LogoUploadRowProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const nameId = `logo-name-${itemKey}`;
  const statusId = `logo-status-${itemKey}`;
  const pickId = `logo-pick-${itemKey}`;
  const retryId = `logo-retry-${itemKey}`;
  const skipId = `logo-skip-${itemKey}`;
  const uploading = status?.kind === "uploading";
  const statusPill = status ? STATUS_PILL[status.kind] : null;

  return (
    <li className="brand-list__row" role="group" aria-labelledby={nameId}>
      <div className="brand-list__preview">
        {previewUrl ? (
          <img src={previewUrl} alt="" className="brand-list__img" />
        ) : (
          <span className="cell-meta">Sin logo</span>
        )}
      </div>
      <div className="brand-list__meta">
        <span id={nameId} className="cell-strong">
          {title}
        </span>{" "}
        {pill}
        <div>
          <span id={statusId} className="cell-meta">
            {statusPill && <span className={statusPill.className}>{statusPill.label}</span>}
            {status?.message ? ` ${status.message}` : null}
          </span>
        </div>
      </div>
      <div className="brand-list__actions">
        <input
          ref={inputRef}
          type="file"
          hidden
          tabIndex={-1}
          aria-hidden="true"
          accept={LOGO_ACCEPT_ATTR}
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Reset so picking the same file again still fires onChange
            // (browsers suppress the event on an identical selection).
            e.target.value = "";
            if (file) onPick(file);
          }}
        />
        <button
          id={pickId}
          type="button"
          className={`btn btn--sm ${previewUrl ? "btn--secondary" : "btn--primary"}`}
          aria-labelledby={`${pickId} ${nameId}`}
          aria-describedby={statusId}
          aria-disabled={uploading ? "true" : undefined}
          disabled={disabled && !uploading}
          onClick={() => {
            if (uploading || disabled) return;
            inputRef.current?.click();
          }}
        >
          {primaryLabel}
        </button>
        {status?.kind === "error" && onRetry && (
          <button
            id={retryId}
            type="button"
            className="btn btn--secondary btn--sm"
            aria-labelledby={`${retryId} ${nameId}`}
            aria-describedby={statusId}
            disabled={disabled}
            onClick={onRetry}
          >
            Reintentar
          </button>
        )}
        {onSkip && status?.kind !== "done" && status?.kind !== "skipped" && !uploading && (
          <button
            id={skipId}
            type="button"
            className="btn btn--ghost btn--sm"
            aria-labelledby={`${skipId} ${nameId}`}
            aria-describedby={statusId}
            disabled={disabled}
            onClick={onSkip}
          >
            Omitir
          </button>
        )}
        {extraActions}
      </div>
    </li>
  );
}
